// Client layer (TS-2.5.03). The project fields form itself - BRD section 6.1's whole field list
// minus the four the server owns (key/concurrencyToken/updatedAtUtc/updatedBy, rendered
// read-only). Team and Status are the two of this form's fields the owner ruled CLOSED
// (2026-10-03): each renders as a `VocabularySelect` (see that component's header for why a
// native `<select>` replaced the old `<input list>` + `<datalist>` pair, and why a stored value
// outside the active vocabulary still shows up, selected, as "(not in list)" rather than being
// silently changed). This is a UI-only constraint - the save API still accepts and echoes back an
// unlisted value unchanged (D-011/D-195/D-229 item 3), and the workbook IMPORT path still accepts
// one with a warning rather than a refusal.
//
// Owner/Business owner stay OPEN fields (step two of the same ruling): a project lead's name can
// be anything, so each renders as a `SuggestionInput` instead - the free-text combobox that
// replaced their own `<input list>` + `<datalist>` pair for the same invisible-vocabulary reason
// (see that component's header). Both share ONE suggestion pool, built once below.
import { DateField } from "../../components/DateField";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { SuggestionInput } from "../../components/SuggestionInput";
import type { SuggestionInputOption } from "../../components/SuggestionInput";
import { VocabularySelect } from "../../components/VocabularySelect";
import type { ProjectOwnerSummary, ReferenceValue, ValidationIssue } from "../../api/generated/contracts";
import type { ProjectDraft } from "./lib/projectDraft";
import { businessOwnerForProductKey, labelForProductKey } from "./lib/productOptions";
import type { ProductOption } from "./lib/productOptions";
import { interpretationForField } from "./lib/saveBanners";
import { strings } from "../../strings";

export interface ProjectFormProps {
  readonly draft: ProjectDraft;
  readonly onChange: (patch: Partial<ProjectDraft>) => void;
  readonly readOnly: boolean;
  readonly issues: readonly ValidationIssue[];
  readonly teamOptions: readonly ReferenceValue[];
  readonly statusOptions: readonly ReferenceValue[];
  readonly ownerOptions: readonly ProjectOwnerSummary[];
  readonly placeholderOptions: ReadonlyArray<{ token: string; label: string }>;
  readonly productOptions: readonly ProductOption[];
}

const S = strings.projects.detail;
const F = strings.projects.detail.fields;

/** D-279's inheritance display, computed once and shared by the edit field's description slot and
 *  the read-only view - the assigned product's business owner name, or null when there is no
 *  product assigned or that product names nobody. Never used to PRE-FILL the input; only to
 *  describe what an empty one currently means. */
function inheritedBusinessOwner(draft: ProjectDraft, productOptions: readonly ProductOption[]): string | null {
  return businessOwnerForProductKey(productOptions, draft.productKey);
}

export function ProjectForm({
  draft,
  onChange,
  readOnly,
  issues,
  teamOptions,
  statusOptions,
  ownerOptions,
  placeholderOptions,
  productOptions,
}: ProjectFormProps) {
  // D-279: "both people columns feed ONE suggestion pool" - GET /api/projects/owners already
  // aggregates Owner and BusinessOwner spellings together, so Owner and Business owner below share
  // this one list rather than each fetching or building their own.
  const ownerSuggestions: readonly SuggestionInputOption[] = ownerOptions.map((owner) => ({
    value: owner.displayName,
    label: owner.displayName,
    hint: `(${owner.projectCount} ${S.ownerSuggestionUnit}${owner.projectCount === 1 ? "" : "s"})`,
  }));

  if (readOnly) {
    return (
      <dl className="eg-project-form eg-project-form--readonly">
        <dt>{F.name}</dt>
        <dd>{draft.name || "—"}</dd>
        <dt>{F.team}</dt>
        <dd>{draft.rawTeam || "—"}</dd>
        <dt>{F.owner}</dt>
        <dd>{draft.owner || "—"}</dd>
        <dt>{F.businessOwner}</dt>
        <dd>{draft.businessOwner || inheritedBusinessOwner(draft, productOptions) || "—"}</dd>
        <dt>{F.product}</dt>
        <dd>{labelForProductKey(productOptions, draft.productKey) ?? S.productNone}</dd>
        <dt>{F.status}</dt>
        <dd>{draft.rawStatus || "—"}</dd>
        <dt>{F.percentComplete}</dt>
        <dd>{draft.rawPercentComplete || "—"}</dd>
        <dt>{F.originalGoLive}</dt>
        <dd>{draft.rawOriginalGoLive || "—"}</dd>
        <dt>{F.currentGoLive}</dt>
        <dd>{draft.rawCurrentGoLive || "—"}</dd>
        <dt>{F.businessProjectId}</dt>
        <dd>{draft.businessProjectId || "—"}</dd>
        <dt>{F.subgroup}</dt>
        <dd>{draft.subgroup || "—"}</dd>
        <dt>{F.description}</dt>
        <dd>{draft.description || "—"}</dd>
        <dt>{F.featureDetails}</dt>
        <dd>{draft.featureDetails || "—"}</dd>
      </dl>
    );
  }

  return (
    <div className="eg-project-form">
      <FormField id="eg-project-name" label={F.name} required>
        <input
          type="text"
          value={draft.name}
          required
          onChange={(event) => onChange({ name: event.target.value })}
          {...formFieldControlProps("eg-project-name")}
        />
      </FormField>

      <FormField id="eg-project-team" label={F.team}>
        <VocabularySelect
          id="eg-project-team"
          value={draft.rawTeam}
          onChange={(next) => onChange({ rawTeam: next })}
          options={teamOptions}
        />
      </FormField>

      <FormField id="eg-project-owner" label={F.owner}>
        <SuggestionInput
          id="eg-project-owner"
          value={draft.owner}
          onChange={(next) => onChange({ owner: next })}
          suggestions={ownerSuggestions}
        />
      </FormField>

      <FormField
        id="eg-project-business-owner"
        label={F.businessOwner}
        // D-279: an empty field means "inherit the assigned product's business owner" - shown here
        // rather than pre-filled into the input, so a typed value and an inherited one stay
        // visibly distinct (an operator who clears this box is choosing to inherit again, not
        // looking at a field that already carries a name). Nothing is shown when the field itself
        // is not empty, or when there is no product assigned, or that product names nobody.
        description={
          draft.businessOwner.trim().length === 0
            ? (() => {
                const productLabel = labelForProductKey(productOptions, draft.productKey);
                const inherited = inheritedBusinessOwner(draft, productOptions);
                return productLabel && inherited ? S.businessOwnerInherited(productLabel, inherited) : undefined;
              })()
            : undefined
        }
      >
        <SuggestionInput
          id="eg-project-business-owner"
          value={draft.businessOwner}
          onChange={(next) => onChange({ businessOwner: next })}
          suggestions={ownerSuggestions}
        />
      </FormField>

      <FormField id="eg-project-product" label={F.product}>
        <select
          value={draft.productKey ?? ""}
          onChange={(event) => onChange({ productKey: event.target.value === "" ? null : event.target.value })}
          {...formFieldControlProps("eg-project-product")}
        >
          <option value="">{S.productNone}</option>
          {productOptions.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
              {option.isRetired ? S.productRetiredSuffix : ""}
            </option>
          ))}
        </select>
      </FormField>

      <FormField id="eg-project-status" label={F.status}>
        <VocabularySelect
          id="eg-project-status"
          value={draft.rawStatus}
          onChange={(next) => onChange({ rawStatus: next })}
          options={statusOptions}
        />
      </FormField>

      <FormField id="eg-project-percent" label={F.percentComplete} description="0–100, or a fraction like 0.5.">
        <input
          type="text"
          inputMode="decimal"
          value={draft.rawPercentComplete}
          onChange={(event) => onChange({ rawPercentComplete: event.target.value })}
          {...formFieldControlProps("eg-project-percent")}
        />
      </FormField>

      <FormField id="eg-project-original-go-live" label={F.originalGoLive}>
        <DateField
          id="eg-project-original-go-live"
          label={F.originalGoLive}
          value={draft.rawOriginalGoLive}
          onChange={(next) => onChange({ rawOriginalGoLive: next })}
          placeholderOptions={placeholderOptions}
          interpretation={interpretationForField(issues, "RawOriginalGoLive")}
        />
      </FormField>

      <FormField id="eg-project-current-go-live" label={F.currentGoLive}>
        <DateField
          id="eg-project-current-go-live"
          label={F.currentGoLive}
          value={draft.rawCurrentGoLive}
          onChange={(next) => onChange({ rawCurrentGoLive: next })}
          placeholderOptions={placeholderOptions}
          interpretation={interpretationForField(issues, "RawCurrentGoLive")}
        />
      </FormField>

      <FormField id="eg-project-business-id" label={F.businessProjectId}>
        <input
          type="text"
          inputMode="numeric"
          value={draft.businessProjectId}
          onChange={(event) => onChange({ businessProjectId: event.target.value })}
          {...formFieldControlProps("eg-project-business-id")}
        />
      </FormField>

      <FormField id="eg-project-subgroup" label={F.subgroup}>
        <input
          type="text"
          value={draft.subgroup}
          onChange={(event) => onChange({ subgroup: event.target.value })}
          {...formFieldControlProps("eg-project-subgroup")}
        />
      </FormField>

      <FormField id="eg-project-description" label={F.description}>
        <textarea
          value={draft.description}
          onChange={(event) => onChange({ description: event.target.value })}
          {...formFieldControlProps("eg-project-description")}
        />
      </FormField>

      <FormField id="eg-project-feature-details" label={F.featureDetails}>
        <textarea
          value={draft.featureDetails}
          onChange={(event) => onChange({ featureDetails: event.target.value })}
          {...formFieldControlProps("eg-project-feature-details")}
        />
      </FormField>
    </div>
  );
}
